import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  BarChart3, Users, Server, TrendingUp, DollarSign,
  ArrowUpRight, ArrowDownRight, Layers,
} from 'lucide-react';

interface DashboardStats {
  totalUsers: number;
  activeInstances: number;
  signupsThisMonth: number;
  revenueThisMonth: number;
  userGrowthPercent: number;
  instanceGrowthPercent: number;
}

interface ProviderDistribution {
  provider: string;
  count: number;
  percentage: number;
}

interface PlanDistribution {
  plan: string;
  count: number;
  percentage: number;
}

interface RevenueBreakdown {
  plan: string;
  count: number;
  revenue: number;
}

const DATE_RANGES = [
  { key: '7d', labelKey: 'analytics.dateRange7d' },
  { key: '30d', labelKey: 'analytics.dateRange30d' },
  { key: '90d', labelKey: 'analytics.dateRange90d' },
];

export function AdminAnalytics() {
  const { t, i18n } = useTranslation();

  const [dateRange, setDateRange] = useState('30d');

  const days = dateRange === '7d' ? 7 : dateRange === '90d' ? 90 : 30;

  // Dashboard stats
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['analytics-dashboard', dateRange],
    queryFn: async () => {
      const { data } = await api.get('/analytics/dashboard');
      return data;
    },
  });

  const stats: DashboardStats = dashboardData || {
    totalUsers: 0,
    activeInstances: 0,
    signupsThisMonth: 0,
    revenueThisMonth: 0,
    userGrowthPercent: 0,
    instanceGrowthPercent: 0,
  };

  // Growth data
  const { data: _growthData } = useQuery({
    queryKey: ['analytics-growth', days],
    queryFn: async () => {
      const { data } = await api.get(`/analytics/growth?days=${days}`);
      return data;
    },
  });

  // Provider distribution
  const { data: providersData } = useQuery({
    queryKey: ['analytics-providers'],
    queryFn: async () => {
      const { data } = await api.get('/analytics/providers');
      return data;
    },
  });

  const providers: ProviderDistribution[] = providersData?.providers || [];

  // Plan distribution
  const { data: plansData } = useQuery({
    queryKey: ['analytics-plans'],
    queryFn: async () => {
      const { data } = await api.get('/analytics/plans');
      return data;
    },
  });

  const plans: PlanDistribution[] = plansData?.plans || [];

  // Revenue breakdown
  const { data: revenueData } = useQuery({
    queryKey: ['analytics-revenue', dateRange],
    queryFn: async () => {
      const { data } = await api.get('/analytics/revenue');
      return data;
    },
  });

  const revenueBreakdown: RevenueBreakdown[] = revenueData?.breakdown || [];

  const formatCurrency = (amount: number): string => {
    if (i18n.language === 'ja') return `\u00a5${amount.toLocaleString()}`;
    return `$${amount.toLocaleString()}`;
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num.toLocaleString();
  };

  const statCards = [
    {
      title: t('analytics.totalUsers'),
      value: formatNumber(stats.totalUsers),
      change: stats.userGrowthPercent,
      icon: Users,
    },
    {
      title: t('analytics.activeInstances'),
      value: formatNumber(stats.activeInstances),
      change: stats.instanceGrowthPercent,
      icon: Server,
    },
    {
      title: t('analytics.signupsThisMonth'),
      value: formatNumber(stats.signupsThisMonth),
      change: null,
      icon: TrendingUp,
    },
    {
      title: t('analytics.revenueThisMonth'),
      value: formatCurrency(stats.revenueThisMonth),
      change: null,
      icon: DollarSign,
    },
  ];

  const providerColors: Record<string, string> = {
    Hetzner: 'bg-emerald-500',
    AWS: 'bg-orange-500',
    DigitalOcean: 'bg-blue-500',
    Vultr: 'bg-purple-500',
    Linode: 'bg-green-500',
  };

  const planColors: Record<string, string> = {
    Free: 'bg-gray-400',
    Starter: 'bg-emerald-500',
    Pro: 'bg-blue-500',
    Business: 'bg-purple-500',
    Enterprise: 'bg-amber-500',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {t('analytics.title')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('analytics.subtitle')}
            </p>
          </div>
        </div>

        {/* Date range picker */}
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {DATE_RANGES.map((range) => (
            <Button
              key={range.key}
              variant="ghost"
              size="sm"
              onClick={() => setDateRange(range.key)}
              className={cn(
                'rounded-md px-3 h-8 text-xs font-medium transition-all',
                dateRange === range.key
                  ? 'bg-white dark:bg-gray-800 text-emerald-600 shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t(range.labelKey)}
            </Button>
          ))}
        </div>
      </div>

      <Separator />

      {/* Stats cards */}
      {dashboardLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((card, idx) => (
            <Card
              key={idx}
              className="transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/5 hover:border-emerald-200 dark:hover:border-emerald-800"
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">{card.title}</span>
                  <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <card.icon className="h-4 w-4 text-emerald-500" />
                  </div>
                </div>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-bold">{card.value}</span>
                  {card.change !== null && (
                    <span
                      className={cn(
                        'flex items-center gap-0.5 text-xs font-medium',
                        card.change >= 0 ? 'text-emerald-600' : 'text-red-500',
                      )}
                    >
                      {card.change >= 0 ? (
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      ) : (
                        <ArrowDownRight className="h-3.5 w-3.5" />
                      )}
                      {Math.abs(card.change).toFixed(1)}%
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* User Growth Chart (placeholder) */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium">
              {t('analytics.userGrowth')}
            </CardTitle>
            <Badge variant="secondary" className="text-[11px]">
              {t('analytics.lastNDays', { days })}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative h-52 w-full rounded-lg overflow-hidden bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/20 dark:to-gray-900">
            {/* Mock chart area with gradient bars */}
            <div className="absolute inset-0 flex items-end justify-between px-4 pb-4 gap-1">
              {Array.from({ length: days <= 7 ? 7 : days <= 30 ? 15 : 20 }, (_, i) => {
                const height = 20 + Math.random() * 60 + (i / (days <= 7 ? 7 : days <= 30 ? 15 : 20)) * 20;
                return (
                  <div
                    key={i}
                    className="flex-1 rounded-t-sm bg-gradient-to-t from-emerald-500 to-emerald-400 opacity-70 hover:opacity-100 transition-opacity"
                    style={{ height: `${height}%` }}
                  />
                );
              })}
            </div>
            {/* Overlay gradient */}
            <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white/80 to-transparent dark:from-gray-900/80" />
            {/* Trend line hint */}
            <div className="absolute top-4 right-4">
              <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                <TrendingUp className="h-3.5 w-3.5" />
                {t('analytics.growthTrend')}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Distribution sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Provider Distribution */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">
              {t('analytics.providerDistribution')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {providers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('analytics.noData')}
              </p>
            ) : (
              <div className="space-y-4">
                {providers.map((provider) => (
                  <div key={provider.provider} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{provider.provider}</span>
                      <span className="text-muted-foreground">
                        {provider.count} ({provider.percentage}%)
                      </span>
                    </div>
                    <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all duration-500',
                          providerColors[provider.provider] || 'bg-emerald-500',
                        )}
                        style={{ width: `${provider.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plan Distribution */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">
              {t('analytics.planDistribution')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {plans.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('analytics.noData')}
              </p>
            ) : (
              <div className="space-y-4">
                {plans.map((plan) => (
                  <div key={plan.plan} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{plan.plan}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {plan.count} {t('analytics.users')}
                        </Badge>
                      </div>
                      <span className="text-muted-foreground">{plan.percentage}%</span>
                    </div>
                    <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all duration-500',
                          planColors[plan.plan] || 'bg-emerald-500',
                        )}
                        style={{ width: `${plan.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Breakdown Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium">
              {t('analytics.revenueBreakdown')}
            </CardTitle>
            <Layers className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          {revenueBreakdown.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              {t('analytics.noData')}
            </p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="text-left p-3 font-medium text-muted-foreground">
                      {t('analytics.plan')}
                    </th>
                    <th className="text-right p-3 font-medium text-muted-foreground">
                      {t('analytics.subscriptions')}
                    </th>
                    <th className="text-right p-3 font-medium text-muted-foreground">
                      {t('analytics.revenue')}
                    </th>
                    <th className="text-right p-3 font-medium text-muted-foreground">
                      {t('analytics.share')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {revenueBreakdown.map((row) => {
                    const totalRevenue = revenueBreakdown.reduce((sum, r) => sum + r.revenue, 0);
                    const share = totalRevenue > 0 ? ((row.revenue / totalRevenue) * 100).toFixed(1) : '0.0';
                    return (
                      <tr key={row.plan} className="border-b last:border-b-0 hover:bg-muted/30 transition-colors">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div
                              className={cn(
                                'h-2.5 w-2.5 rounded-full',
                                planColors[row.plan] || 'bg-emerald-500',
                              )}
                            />
                            <span className="font-medium">{row.plan}</span>
                          </div>
                        </td>
                        <td className="p-3 text-right text-muted-foreground">
                          {row.count.toLocaleString()}
                        </td>
                        <td className="p-3 text-right font-medium">
                          {formatCurrency(row.revenue)}
                        </td>
                        <td className="p-3 text-right text-muted-foreground">
                          {share}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/30 font-medium">
                    <td className="p-3">{t('analytics.total')}</td>
                    <td className="p-3 text-right">
                      {revenueBreakdown.reduce((sum, r) => sum + r.count, 0).toLocaleString()}
                    </td>
                    <td className="p-3 text-right">
                      {formatCurrency(revenueBreakdown.reduce((sum, r) => sum + r.revenue, 0))}
                    </td>
                    <td className="p-3 text-right">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
